import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
    Box,
    ButtonBase,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import NotificationsRounded from "@mui/icons-material/NotificationsRounded";
import EventRounded from "@mui/icons-material/EventRounded";
import ChatRounded from "@mui/icons-material/ChatRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import CardMembershipRounded from "@mui/icons-material/CardMembershipRounded";
import PriorityHighRounded from "@mui/icons-material/PriorityHighRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import Header from "./Header";
import Footer from "./Footer";
import ColoredIcon from "../ColoredIcon";
import BackIcon from "../../img/Arrow_Left.svg";
import AppNotification, { NotificationType } from "../../api/Notification";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { parseWithOffset } from "../../utils/dayjsUtils";
import { getEnvironment } from "../../utils/getEnvironment";
import { requestNotificationPermission } from "../../utils/pushUtils";
import NotificationPermissionsBanner from "../NotificationPermissionsBanner";
import { useSwipeable } from "react-swipeable";
import EmptyListState from "../EmptyListState";
import TagFilterBar from "../TagFilterBar";
import { FONTS } from "../../constants/styles";

const environment = getEnvironment();

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const PAGE_SIZE = 10;

const FILTER_TAGS = ["All", "Booking", "Chat", "User", "Subscription", "Review"];
const FILTER_DISPLAY = ["All", "Bookings", "Chat", "Users", "Subscriptions", "Review"];

const typeForFilter = (filter: string): NotificationType | null => {
    switch (filter) {
        case "Booking":
            return NotificationType.Booking;
        case "Chat":
            return NotificationType.Chat;
        case "User":
            return NotificationType.User;
        case "Subscription":
            return NotificationType.Subscription;
        case "Review":
            return NotificationType.ConfirmationReview;
        default:
            return null;
    }
};

const getTypeIcon = (type: NotificationType) => {
    switch (type) {
        case NotificationType.Subscription:
            return <CardMembershipRounded sx={{ fontSize: 18 }} />;
        case NotificationType.Booking:
            return <EventRounded sx={{ fontSize: 18 }} />;
        case NotificationType.Chat:
            return <ChatRounded sx={{ fontSize: 18 }} />;
        case NotificationType.User:
            return <PersonRounded sx={{ fontSize: 18 }} />;
        case NotificationType.ConfirmationReview:
            return <PriorityHighRounded sx={{ fontSize: 18 }} />;
        default:
            return <NotificationsRounded sx={{ fontSize: 18 }} />;
    }
};

const getTypeTitle = (type: NotificationType) => {
    switch (type) {
        case NotificationType.Subscription:
            return "Subscription";
        case NotificationType.Booking:
            return "Booking";
        case NotificationType.Chat:
            return "Chat";
        case NotificationType.User:
            return "User";
        case NotificationType.ConfirmationReview:
            return "Attention Required";
        default:
            return "Notification";
    }
};

const NotificationsList = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();
    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [page, setPage] = useState(1);
    const [totalNotifications, setTotalNotifications] = useState<number | null>(
        null
    );
    const [loading, setLoading] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState("All");

    const observerRef = useRef<HTMLDivElement | null>(null);

    /* ── Single scroll (Pattern A) ── */
    const scrollRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const [scrollY, setScrollY] = useState(0);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setScrollY(el.scrollTop);
    }, []);

    // On filter change, scroll just enough to hide the hero
    useLayoutEffect(() => {
        const el = scrollRef.current;
        const toolbar = toolbarRef.current;
        if (!el || !toolbar) return;
        const headerHeight = 56;
        const target = toolbar.offsetTop - headerHeight;
        if (el.scrollTop < target) return;
        el.scrollTop = target;
    }, [selectedFilter]);

    const loadMoreNotifications = useCallback(async () => {
        if (
            !uuid ||
            !brand_id ||
            loading ||
            totalNotifications === 0 ||
            (totalNotifications !== null &&
                notifications.length >= totalNotifications)
        )
            return;
        setLoading(true);

        try {
            const response = await AppNotification.list(
                uuid,
                brand_id,
                (page - 1) * PAGE_SIZE,
                PAGE_SIZE
            );

            if (response && totalNotifications === null) {
                setTotalNotifications(response.total);
            }

            if (response?.notifications.length) {
                setNotifications((prev) => {
                    const uniqueNotifications = new Map(
                        prev.map((n) => [n.key, n])
                    );
                    response.notifications.forEach((n) =>
                        uniqueNotifications.set(n.key, n)
                    );
                    return Array.from(uniqueNotifications.values());
                });

                setPage((prevPage) => prevPage + 1);

                if (
                    notifications.length + response.notifications.length >=
                    response.total
                ) {
                    setTotalNotifications(
                        notifications.length + response.notifications.length
                    );
                }
            }
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
        } finally {
            setLoading(false);
        }
    }, [
        uuid,
        brand_id,
        page,
        loading,
        totalNotifications,
        notifications.length,
    ]);

    useEffect(() => {
        if (!uuid || !brand_id) {
            navigate("/");
            return;
        }
        if (totalNotifications === null && notifications.length === 0) {
            loadMoreNotifications();
        }
    }, [
        uuid,
        brand_id,
        navigate,
        loadMoreNotifications,
        notifications.length,
        totalNotifications,
    ]);

    // Infinite scroll
    useEffect(() => {
        if (
            loading ||
            !observerRef.current ||
            (totalNotifications !== null &&
                notifications.length >= totalNotifications)
        )
            return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreNotifications();
                }
            },
            { rootMargin: "100px" }
        );

        if (observerRef.current) observer.observe(observerRef.current);
        return () => observer.disconnect();
    }, [
        loadMoreNotifications,
        loading,
        notifications.length,
        totalNotifications,
    ]);

    const onRequestPermission = async () => {
        try {
            await requestNotificationPermission(uuid, environment.vapid_key);
        } catch (error) {
            console.error("Failed to request notification permission:", error);
        }
    };

    const onNotificationClick = (notificationKey: string) => {
        navigate(`/notifications/${notificationKey}`);
    };

    const onNotificationDelete = (notificationKey: string) => {
        setNotifications((prev) =>
            prev.filter((n) => n.key !== notificationKey)
        );
    };

    const sorted = notifications
        .sort((a, b) =>
            parseWithOffset(b.created).diff(parseWithOffset(a.created))
        );

    const filtered =
        selectedFilter === "All"
            ? sorted
            : sorted.filter(
                  (n) => n.type === typeForFilter(selectedFilter)
              );

    const unreadCount = notifications.filter((n) => n.viewed === null).length;

    // Stagger index
    let globalIndex = 0;

    return (
        <>
            <Header scrollY={scrollY} />

            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                flex={1}
                sx={{ overflowY: "auto", overflowX: "hidden" }}
                bgcolor="white"
            >
                {/* ── Hero ── */}
                <Stack
                    width="100%"
                    sx={{
                        background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                        pt: "calc(72px + env(safe-area-inset-top))",
                        pb: "40px",
                        px: 2,
                        boxSizing: "border-box",
                        position: "relative",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.04,
                            backgroundImage: NOISE_TEXTURE,
                            backgroundRepeat: "repeat",
                            backgroundSize: "128px 128px",
                            pointerEvents: "none",
                        },
                    }}
                >
                    <ButtonBase
                        onClick={() => navigate("/")}
                        sx={{
                            position: "absolute",
                            top: "calc(12px + env(safe-area-inset-top))",
                            left: 10,
                            zIndex: 2,
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "rgba(60,60,60,0.35)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <ColoredIcon icon={BackIcon} color="white" size="28px" />
                    </ButtonBase>
                    <Box
                        sx={{
                            bgcolor: "rgba(255,255,255,0.18)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            borderRadius: "16px",
                            px: 2,
                            py: 1.5,
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        <Stack spacing={1}>
                            <Typography
                                fontFamily={FONTS.display}
                                fontWeight={700}
                                fontSize="28px"
                                lineHeight={1.2}
                                color="white"
                            >
                                Notifications
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {notifications.length > 0 && (
                                    <Box
                                        sx={{
                                            bgcolor: "rgba(255,255,255,0.15)",
                                            borderRadius: "12px",
                                            px: 1.25,
                                            py: 0.25,
                                        }}
                                    >
                                        <Typography
                                            fontSize="12px"
                                            fontWeight={600}
                                            color="white"
                                        >
                                            {notifications.length} notification
                                            {notifications.length !== 1 ? "s" : ""}
                                        </Typography>
                                    </Box>
                                )}
                                {unreadCount > 0 && (
                                    <Box
                                        sx={{
                                            bgcolor: "rgba(255,255,255,0.15)",
                                            borderRadius: "12px",
                                            px: 1.25,
                                            py: 0.25,
                                        }}
                                    >
                                        <Typography
                                            fontSize="12px"
                                            fontWeight={600}
                                            color="white"
                                        >
                                            {unreadCount} new
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>

                {/* ── Bottom sheet ── */}
                <Stack
                    sx={{
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        mt: "-24px",
                        bgcolor: "white",
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    {/* Sticky toolbar */}
                    <Box
                        ref={toolbarRef}
                        sx={{
                            position: "sticky",
                            top: "calc(56px + env(safe-area-inset-top))",
                            zIndex: 4,
                            bgcolor: "white",
                            borderTopLeftRadius: "24px",
                            borderTopRightRadius: "24px",
                        }}
                    >
                        <Box sx={{ pt: 2, pb: 1.5 }}>
                            <TagFilterBar
                                tags={FILTER_TAGS}
                                displayTags={FILTER_DISPLAY}
                                selectedTag={selectedFilter}
                                onSelectTag={setSelectedFilter}
                            />
                        </Box>
                    </Box>

                    {/* ── List content ── */}
                    <Box>
                        {!loading && filtered.length === 0 ? (
                            <EmptyListState
                                primaryText="No notifications yet"
                                secondaryText="You'll see updates here when someone books, messages you, or when we have something important to share."
                                icon={
                                    <NotificationsRounded
                                        sx={{
                                            fontSize: 28,
                                            color: primary,
                                            opacity: 0.5,
                                        }}
                                    />
                                }
                            />
                        ) : (
                            <Stack
                                spacing={1}
                                sx={{
                                    px: 2,
                                    pt: 0.5,
                                    pb: "200px",
                                    "@keyframes fadeSlideIn": {
                                        from: {
                                            opacity: 0,
                                            transform: "translateY(12px)",
                                        },
                                        to: {
                                            opacity: 1,
                                            transform: "translateY(0)",
                                        },
                                    },
                                    animation: "fadeSlideIn 0.3s ease-out",
                                }}
                            >
                                <NotificationPermissionsBanner
                                    onRequestPermission={onRequestPermission}
                                />
                                {filtered.map((x) => {
                                    const idx = globalIndex++;
                                    return (
                                        <NotificationItem
                                            key={x.key}
                                            notification={x}
                                            index={idx}
                                            onClick={() =>
                                                onNotificationClick(x.key)
                                            }
                                            onDelete={() =>
                                                onNotificationDelete(x.key)
                                            }
                                        />
                                    );
                                })}
                                <div ref={observerRef} style={{ height: 1 }} />
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </Stack>

            <Footer />
        </>
    );
};

/* ── Notification Item ── */

const NotificationItem = (props: {
    notification: AppNotification;
    index: number;
    onClick: () => void;
    onDelete: () => void;
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const { notification, index, onClick, onDelete } = props;

    const [swiped, setSwiped] = useState(false);

    const isNew = notification.viewed === null;
    const title = getTypeTitle(notification.type);
    const isAttention =
        notification.type === NotificationType.ConfirmationReview;

    const iconColor = isAttention ? theme.palette.warning.dark : primary;
    const iconBg = isAttention
        ? alpha(theme.palette.warning.main, 0.1)
        : alpha(primary, 0.1);

    const handlers = useSwipeable({
        onSwipedRight: () => setSwiped(true),
        onSwipedLeft: () => setSwiped(false),
        preventScrollOnSwipe: true,
        trackMouse: true,
    });

    const truncateText = (text: string, maxLength: number) =>
        text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

    return (
        <Box
            {...handlers}
            sx={{
                position: "relative",
                transition: "transform 0.3s ease",
                transform: swiped ? "translateX(64px)" : "translateX(0)",
                "@keyframes fadeSlideUp": {
                    from: { opacity: 0, transform: "translateY(10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: `fadeSlideUp 0.25s ease-out ${index * 30}ms both`,
            }}
        >
            {/* Delete action (behind the card) */}
            {swiped && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: "-64px",
                        width: 56,
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <IconButton
                        onClick={onDelete}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "12px",
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                        }}
                    >
                        <DeleteOutlineRounded
                            sx={{
                                fontSize: 20,
                                color: theme.palette.error.main,
                            }}
                        />
                    </IconButton>
                </Box>
            )}

            <ButtonBase
                onClick={onClick}
                sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-start",
                    textAlign: "left",
                    p: 1.5,
                    borderRadius: "16px",
                    bgcolor: isNew ? alpha(primary, 0.03) : "transparent",
                    transition: "background-color 0.2s ease",
                }}
            >
                {/* Icon box */}
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        bgcolor: iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        color: iconColor,
                        position: "relative",
                    }}
                >
                    {getTypeIcon(notification.type)}
                    {/* Unread dot */}
                    {isNew && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: -2,
                                right: -2,
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: primary,
                                border: "2px solid white",
                            }}
                        />
                    )}
                </Box>

                {/* Content */}
                <Stack flex={1} ml={1.5} spacing={0.25} overflow="hidden">
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography
                            fontWeight={isNew ? 600 : 500}
                            fontSize="14px"
                            lineHeight="20px"
                            color={isAttention ? theme.palette.warning.dark : "text.primary"}
                        >
                            {title}
                        </Typography>
                        <Typography
                            fontSize="11px"
                            color="text.disabled"
                            flexShrink={0}
                            ml={1}
                        >
                            {parseWithOffset(notification.created).format(
                                "MMM D, HH:mm"
                            )}
                        </Typography>
                    </Stack>
                    <Typography
                        fontSize="13px"
                        lineHeight="19px"
                        color="text.secondary"
                        sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {truncateText(notification.message, 100)}
                    </Typography>
                </Stack>
            </ButtonBase>
        </Box>
    );
};

export default NotificationsList;
